/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useLazyQuery } from '@apollo/client'
import { Search } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Divider,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  SABRE_GET_PRODUCTS_SEARCH,
  type SabreGetProductsSearchQuery,
  type SabreGetProductsSearchQueryVariables,
} from '@dai/graphql'
import React from 'react'

import useT from '@/i18n/useT'

import {
  getSearchProductDisplayName,
  type SearchProduct,
} from './labelingHelpers'
import ProductImageBox from './ProductImageBox'
import {
  readRecentReplacementProducts,
  RECENT_PRODUCTS_UPDATED_EVENT,
  recentReplacementProductMatchesQuery,
  rememberRecentReplacementProduct,
  type ReplacementProductOption,
} from './recentReplacementProducts'

type ProductSearchAutocompleteProps = {
  value: SearchProduct | null
  onChange: (product: SearchProduct | null) => void
  label?: string
  placeholder?: string
  fullWidth?: boolean
  size?: 'small' | 'medium'
  minWidth?: number
  disabled?: boolean
  suggestedProducts?: SearchProduct[]
}

const ProductSearchAutocomplete: React.FC<ProductSearchAutocompleteProps> = ({
  value,
  onChange,
  label,
  placeholder,
  fullWidth = false,
  size = 'medium',
  minWidth = 360,
  disabled = false,
  suggestedProducts = [],
}) => {
  const { t } = useT()
  const [input, setInput] = React.useState('')
  const [searchOptions, setSearchOptions] = React.useState<SearchProduct[]>([])
  const [recentProducts, setRecentProducts] = React.useState<SearchProduct[]>(
    () => readRecentReplacementProducts(),
  )
  const latestRequestRef = React.useRef(0)
  const [searchProducts, { loading }] = useLazyQuery<
    SabreGetProductsSearchQuery,
    SabreGetProductsSearchQueryVariables
  >(SABRE_GET_PRODUCTS_SEARCH, {
    context: { endPoint: 'sabre' },
    fetchPolicy: 'network-only',
  })

  React.useEffect(() => {
    const refreshRecents = () =>
      setRecentProducts(readRecentReplacementProducts())
    window.addEventListener(RECENT_PRODUCTS_UPDATED_EVENT, refreshRecents)
    return () =>
      window.removeEventListener(RECENT_PRODUCTS_UPDATED_EVENT, refreshRecents)
  }, [])

  React.useEffect(() => {
    setInput(value ? getSearchProductDisplayName(value) ?? value.upcA : '')
  }, [value])

  React.useEffect(() => {
    const query = input.trim()
    latestRequestRef.current += 1
    const requestId = latestRequestRef.current
    if (!query || value) {
      setSearchOptions([])
      return
    }

    const timeout = window.setTimeout(() => {
      void searchProducts({
        variables: {
          input: { searchTerm: query, limit: 20, onlyActive: true },
        },
      })
        .then(async active => {
          if (latestRequestRef.current !== requestId) return
          const activeResults = active.data?.products.results ?? []
          if (activeResults.length) {
            setSearchOptions(activeResults)
            return
          }
          const fallback = await searchProducts({
            variables: { input: { searchTerm: query, limit: 20 } },
          })
          if (latestRequestRef.current === requestId) {
            setSearchOptions(fallback.data?.products.results ?? [])
          }
        })
        .catch(() => {
          if (latestRequestRef.current === requestId) setSearchOptions([])
        })
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [input, searchProducts, value])

  const options = React.useMemo<ReplacementProductOption[]>(() => {
    const matchingRecent = recentProducts.filter(product =>
      recentReplacementProductMatchesQuery(product, input),
    )
    const recentUpcs = new Set(matchingRecent.map(product => product.upcA))
    const allProducts = [...suggestedProducts, ...searchOptions].filter(
      product =>
        !recentUpcs.has(product.upcA) &&
        recentReplacementProductMatchesQuery(product, input),
    )
    const seenUpcs = new Set<string>()
    return [
      ...matchingRecent.map(product => ({
        ...product,
        resultGroup: 'Recent' as const,
      })),
      ...allProducts
        .filter(product => {
          if (seenUpcs.has(product.upcA)) return false
          seenUpcs.add(product.upcA)
          return true
        })
        .map(product => ({
          ...product,
          resultGroup: 'All products' as const,
        })),
    ]
  }, [input, recentProducts, searchOptions, suggestedProducts])

  return (
    <Autocomplete<ReplacementProductOption>
      value={value as ReplacementProductOption | null}
      options={options}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      groupBy={option => option.resultGroup}
      filterOptions={items => items}
      getOptionLabel={option =>
        getSearchProductDisplayName(option) ?? option.upcA
      }
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      inputValue={input}
      onInputChange={(_event, nextInput, reason) => {
        if (reason === 'reset' && value) return
        setInput(nextInput)
        if (reason === 'clear') onChange(null)
      }}
      onChange={(_event, product) => {
        onChange(product)
        if (product) {
          setRecentProducts(current =>
            rememberRecentReplacementProduct(current, product),
          )
        }
      }}
      noOptionsText={
        input.trim()
          ? t('productSearchAutocomplete.noProductsFound', 'No products found')
          : recentProducts.length
            ? t('productSearchAutocomplete.recentProducts', 'Recent products')
            : t(
                'productSearchAutocomplete.startTyping',
                'Start typing a product name or UPC',
              )
      }
      loadingText={t('productSearchAutocomplete.loading', 'Loading…')}
      clearText={t('common.clear', 'Clear')}
      closeText={t('common.close', 'Close')}
      openText={t('common.open', 'Open')}
      renderInput={params => (
        <TextField
          {...params}
          label={
            label ?? t('productSearchAutocomplete.label', 'Search products')
          }
          placeholder={
            placeholder ??
            t(
              'productSearchAutocomplete.placeholder',
              'Try a UPC, preferred name, or brand',
            )
          }
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search fontSize="small" sx={{ mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      renderGroup={params => (
        <Box component="li" key={params.key}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 2, py: 0.75, fontWeight: 700 }}
          >
            {params.group === 'Recent'
              ? t('productSearchAutocomplete.group.recent', 'Recent')
              : t(
                  'productSearchAutocomplete.group.allProducts',
                  'All products',
                )}
          </Typography>
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {params.children}
          </Box>
          <Divider component="div" />
        </Box>
      )}
      renderOption={(props, option) => {
        const optionLabel = getSearchProductDisplayName(option) ?? option.upcA
        return (
          <Box component="li" {...props} key={option.id}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ py: 0.5 }}
            >
              <ProductImageBox
                imageUrl={option.marketingImageUrl}
                size={52}
                alt={optionLabel}
                noImageLabel={t(
                  'productSearchAutocomplete.noImage',
                  'No image',
                )}
                padding={0.5}
                previewImageSize={320}
              />
              <ListItemText
                primary={optionLabel}
                secondary={t('productSearchAutocomplete.upc', {
                  upc: option.upcA,
                  defaultValue: `UPC ${option.upcA}`,
                })}
              />
            </Stack>
          </Box>
        )
      }}
      sx={{ minWidth }}
    />
  )
}

export default ProductSearchAutocomplete
